import { event as gaEvent } from "@/lib/gtag";
import {
  getInternalTrafficContext,
  isInternalTraffic,
} from "@/lib/internal-traffic";
import { getJourneySessionId } from "@/lib/journey-analytics";

export type QuizStudyContext = {
  studySubject: string;
  studyTopic: string;
};

export type QuizLoadFailureReason =
  | "not_found"
  | "load_error"
  | "question_not_found"
  | "empty_question_bank";

type QuizEventParams = {
  quizId: string;
  attemptId?: string;
  surface?: "embedded" | "standalone";
  mode?: "full" | "fix-it" | "quick-check";
  score?: number;
  total?: number;
  questions_answered?: number;
  question_number?: number;
  question_id?: number;
  answer_correct?: boolean;
  placement_id?: string;
  section_id?: string;
  elapsed_seconds?: number;
  failure_reason?: QuizLoadFailureReason;
  wrongIds?: number[];
  studySubject?: string;
  studyTopic?: string;
  [key: string]: unknown;
};

/**
 * Fire a quiz analytics event to both GA4 and the server-side log.
 * Non-blocking - never throws or blocks the UI.
 */
export function trackQuizEvent(
  eventName: string,
  params: QuizEventParams
): void {
  // GA4 (client-side)
  const pct =
    typeof params.score === "number" &&
    typeof params.total === "number" &&
    params.total > 0
      ? Math.round((params.score / params.total) * 100)
      : undefined;

  let journeySessionId: string | undefined;
  let internal = false;
  let internalContext: ReturnType<typeof getInternalTrafficContext> = {};
  try {
    journeySessionId = getJourneySessionId() ?? undefined;
  } catch {
    // Analytics context failures must not break quiz interactions.
  }
  try {
    internal = isInternalTraffic();
  } catch {
    // Default to external delivery when the local marker cannot be read.
  }
  try {
    internalContext = getInternalTrafficContext();
  } catch {
    // The first-party event remains useful without optional audit context.
  }
  const gaParams = {
    quiz_id: params.quizId,
    measurement_contract: "quiz_v2",
    ...(journeySessionId && { journey_session_id: journeySessionId }),
    ...(params.attemptId && { attempt_id: params.attemptId }),
    ...(params.surface && { quiz_surface: params.surface }),
    ...(params.mode && { quiz_mode: params.mode }),
    ...(typeof params.score === "number" && { quiz_score: params.score }),
    ...(typeof params.total === "number" && { quiz_total: params.total }),
    ...(pct !== undefined && { quiz_pct: pct }),
    ...(typeof params.questions_answered === "number" && {
      questions_answered: params.questions_answered,
    }),
    ...(typeof params.question_number === "number" && {
      question_number: params.question_number,
    }),
    ...(typeof params.question_id === "number" && {
      question_id: params.question_id,
    }),
    ...(typeof params.answer_correct === "boolean" && {
      answer_correct: params.answer_correct,
    }),
    ...(params.placement_id && { placement_id: params.placement_id }),
    ...(params.section_id && { section_id: params.section_id }),
    ...(typeof params.elapsed_seconds === "number" && {
      elapsed_seconds: params.elapsed_seconds,
    }),
    ...(params.failure_reason && { failure_reason: params.failure_reason }),
    ...(params.studySubject && { study_subject: params.studySubject }),
    ...(params.studyTopic && { study_topic: params.studyTopic }),
  };

  if (!internal) {
    try {
      gaEvent(eventName, gaParams);
    } catch {
      // One analytics sink must not block the other.
    }
  }

  // Server-side JSONL log (fire-and-forget)
  if (typeof window === "undefined") return;

  let payload: string;
  try {
    payload = JSON.stringify({
      event: eventName,
      eventId: createAnalyticsEventId("quiz-event"),
      quizId: params.quizId,
      measurementContract: "quiz_v2",
      attemptId: params.attemptId,
      journeySessionId,
      trafficType: internalContext.trafficType,
      automationSource: internalContext.automationSource,
      auditRunId: internalContext.auditRunId,
      surface: params.surface,
      mode: params.mode,
      score: params.score,
      total: params.total,
      pct,
      questionsAnswered: params.questions_answered,
      questionNumber: params.question_number,
      questionId: params.question_id,
      answerCorrect: params.answer_correct,
      placementId: params.placement_id,
      sectionId: params.section_id,
      elapsedSeconds: params.elapsed_seconds,
      failureReason: params.failure_reason,
      wrongIds: params.wrongIds,
      studySubject: params.studySubject,
      studyTopic: params.studyTopic,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    });
  } catch {
    return;
  }

  const sendWithFetch = () => {
    try {
      void Promise.resolve(
        fetch("/api/quiz/events", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        })
      ).catch(() => {});
    } catch {
      // Analytics delivery is best effort.
    }
  };

  try {
    if (
      navigator.sendBeacon?.(
        "/api/quiz/events",
        new Blob([payload], { type: "application/json" })
      )
    ) {
      return;
    }
  } catch {
    // Fall back when the browser rejects or cannot queue the beacon.
  }
  sendWithFetch();
}

export function createQuizAttemptId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `quiz-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function createAnalyticsEventId(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Fire a GA4-only impression event when a post-quiz CTA is rendered on the
 * results screen. Gives quiz_whatsapp_lead (and post-quiz telegram_click) a
 * denominator: a bare 0 clicks is otherwise ambiguous between "CTA never seen"
 * and "seen but not clicked".
 *
 * GA4-only by design because this denominator belongs to the broader CTA
 * taxonomy rather than the first-party quiz journey log. Non-blocking; never
 * throws.
 */
export function trackQuizCtaView(params: {
  quizId: string;
  cta: "whatsapp" | "telegram";
  score: number;
  total: number;
  studyContext?: QuizStudyContext | null;
}): void {
  try {
    const pct =
      typeof params.total === "number" && params.total > 0
        ? Math.round((params.score / params.total) * 100)
        : undefined;

    if (isInternalTraffic()) return;

    gaEvent("quiz_cta_view", {
      quiz_id: params.quizId,
      journey_session_id: getJourneySessionId() ?? undefined,
      cta: params.cta,
      ...(pct !== undefined && {
        quiz_pct: pct,
        score_bucket: pct < 80 ? "lt80" : "gte80",
      }),
      ...(typeof params.score === "number" && { quiz_score: params.score }),
      ...(typeof params.total === "number" && { quiz_total: params.total }),
      ...(params.studyContext?.studySubject && {
        study_subject: params.studyContext.studySubject,
      }),
      ...(params.studyContext?.studyTopic && {
        study_topic: params.studyContext.studyTopic,
      }),
    });
  } catch {
    // Analytics failures must not break the quiz results screen.
  }
}

/**
 * Record a deliberate move from completed practice into the matching notes.
 * GA4-only because the first-party quiz endpoint has a narrow event allowlist.
 */
export function trackQuizNotesReviewClick(params: {
  quizId: string;
  score: number;
  total: number;
  mode: "full" | "fix-it";
  studyContext?: QuizStudyContext | null;
}): void {
  try {
    const pct =
      params.total > 0 ? Math.round((params.score / params.total) * 100) : 0;

    if (isInternalTraffic()) return;

    gaEvent("quiz_notes_review_click", {
      quiz_id: params.quizId,
      journey_session_id: getJourneySessionId() ?? undefined,
      quiz_score: params.score,
      quiz_total: params.total,
      quiz_pct: pct,
      quiz_mode: params.mode,
      source: "results",
      ...(params.studyContext?.studySubject && {
        study_subject: params.studyContext.studySubject,
      }),
      ...(params.studyContext?.studyTopic && {
        study_topic: params.studyContext.studyTopic,
      }),
    });
  } catch {
    // Analytics failures must not break navigation to the notes.
  }
}
